// frontend/src/components/shared/PropertyMap.js
// Uses OpenStreetMap + Leaflet — free, no API key required
// Easy to swap to Google Maps later by replacing this component

import { useEffect, useRef } from 'react';

export default function PropertyMap({ address, city, state, zip, name }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const fullAddress = [address, city, state, zip].filter(Boolean).join(', ');

  useEffect(() => {
    if (!fullAddress || mapInstanceRef.current) return;

    // Dynamically load Leaflet CSS
    if (!document.querySelector('#leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    // Dynamically load Leaflet JS
    const loadLeaflet = () => {
      if (window.L) {
        initMap();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = initMap;
      document.head.appendChild(script);
    };

    const initMap = async () => {
      try {
        // Geocode using OpenStreetMap Nominatim (free)
        const encoded = encodeURIComponent(fullAddress);
        const res = await fetch('https://nominatim.openstreetmap.org/search?format=json&q=' + encoded + '&limit=1');
        const data = await res.json();

        if (!data.length) return;
        const { lat, lon } = data[0];

        if (!mapRef.current) return;

        const L = window.L;
        const map = L.map(mapRef.current).setView([parseFloat(lat), parseFloat(lon)], 16);

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(map);

        const icon = L.divIcon({
          html: '<div style="background:#2563eb;width:14px;height:14px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>',
          iconSize: [14, 14],
          iconAnchor: [7, 7],
          className: '',
        });

        L.marker([parseFloat(lat), parseFloat(lon)], { icon })
          .addTo(map)
          .bindPopup('<strong>' + (name || address) + '</strong><br>' + fullAddress)
          .openPopup();

        mapInstanceRef.current = map;
      } catch (err) {
        console.error('Map error:', err);
      }
    };

    loadLeaflet();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [fullAddress]);

  if (!fullAddress) return null;

  return (
    <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', padding: '1.5rem', marginTop: 20 }}>
      <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', marginBottom: '1rem' }}>📍 Property Location</h2>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>{fullAddress}</div>
      <div
        ref={mapRef}
        style={{ height: 300, borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f1f5f9' }}
      />
      <div style={{ marginTop: 10 }}>
        <a href={'https://www.google.com/maps/search/' + encodeURIComponent(fullAddress)} target="_blank" rel="noreferrer"
          style={{ fontSize: 12, color: '#2563eb', textDecoration: 'none', fontWeight: 600 }}>
          Open in Google Maps →
        </a>
      </div>
    </div>
  );
}
